/**
 * workflowNav — the bridge that lets the sidebar's Workflows level (home → bot →
 * Settings → Workflows) render the conversation-workflow list and drive the two
 * actions that only the open canvas can perform.
 *
 * Why a module store and not React context: `Sidenav` is mounted by
 * `ShellLayout`, ABOVE the page in the tree, so a provider inside
 * `WorkflowEditor` can never reach it. The canvas publishes here on mount and
 * withdraws on unmount; the sidebar subscribes. Same shape of problem the
 * sandbox rail solves with postMessage, but both ends are plain React in one
 * window, so a store is enough.
 *
 * Selection is deliberately NOT in here — opening a workflow is a URL change
 * (`?tab=workflow&wf={id}`), which `Configure` already feeds back into the
 * editor as a prop, so the sidebar rows are ordinary links.
 */
import { useSyncExternalStore } from 'react';
import type { ConversationWorkflowMeta } from '../pages/conversationWorkflowsApi';
import { MAIN_WORKFLOW_NAME } from '../pages/conversationWorkflowsApi';

export interface WorkflowNavState {
  botID: string;
  /** The workflow currently open on the canvas — highlighted, and not callable. */
  activeWorkflowID: string;
  workflows: ConversationWorkflowMeta[];
  /** False until the list resolves, so the sidebar can say so rather than look empty. */
  loaded: boolean;
  /** Open the canvas's "new workflow" dialog (name + optional template). */
  createWorkflow: () => void;
  /** Drop a call to `wf` onto the open canvas as a workflow node. */
  insertWorkflowCall: (wf: ConversationWorkflowMeta) => void;
}

let current: WorkflowNavState | null = null;
const listeners = new Set<() => void>();

/** Publish the open canvas's workflow state, or `null` when no canvas is mounted. */
export function publishWorkflowNav(next: WorkflowNavState | null): void {
  if (current === next) return;
  current = next;
  // Copy: a listener that unsubscribes during notification must not skip a peer.
  for (const listener of Array.from(listeners)) listener();
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

function snapshot(): WorkflowNavState | null {
  return current;
}

/** The open canvas's workflow state; `null` when the editor isn't mounted. */
export function useWorkflowNav(): WorkflowNavState | null {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/**
 * Display order: Main first (it's the entry graph every call starts in), then
 * the rest alphabetically. Case-insensitive on the Main match because the name
 * is operator-editable.
 */
export function orderWorkflowsForNav(workflows: ConversationWorkflowMeta[]): ConversationWorkflowMeta[] {
  const main = MAIN_WORKFLOW_NAME.toLowerCase();
  return [...workflows].sort((a, b) => {
    const aMain = a.name.trim().toLowerCase() === main;
    const bMain = b.name.trim().toLowerCase() === main;
    if (aMain !== bMain) return aMain ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

/** `/b/{botID}/config?tab=workflow&wf={wfID}` — the URL that opens a workflow. */
export function workflowEditorHref(botID: string, workflowID?: string): string {
  const base = `/b/${encodeURIComponent(botID)}/config?tab=workflow`;
  return workflowID ? `${base}&wf=${encodeURIComponent(workflowID)}` : base;
}
